import { Children } from "react";
import { resolveCampImage, resolveCampMobileImage } from "./campImageMap";

function hasRenderableMedia(children) {
  if (children == null) return false;
  return Children.toArray(children).some(
    (node) => node != null && node !== false && node !== true,
  );
}

/**
 * Vertical bar + divider rows. Use anywhere — inside `EventBody` `body`, between sections, etc.
 */
export function EventBodyDetailsList({ items = [], className = "" } = {}) {
  const list = Array.isArray(items) ? items.filter((t) => t != null && String(t).trim() !== "") : [];
  if (list.length === 0) return null;

  const rootClass = ["event-temp-event-body__details-list", className].filter(Boolean).join(" ");

  return (
    <ul className={rootClass}>
      {list.map((text, index) => (
        <li key={index} className="event-temp-event-body__details-item">
          <span className="event-temp-event-body__details-bar" aria-hidden />
          <span className="event-temp-event-body__details-text">{text}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Timetable rows: `time` (left, accent) + `title` / optional `description` (right). Same divider pattern as `EventBodyDetailsList`.
 *
 * @param {{ items?: Array<{ time: string, title: string, description?: string }>, className?: string }} props
 */
export function EventBodyScheduleList({ items = [], className = "" } = {}) {
  const list = Array.isArray(items)
    ? items.filter(
        (row) =>
          row != null &&
          row.time != null &&
          String(row.time).trim() !== "" &&
          row.title != null &&
          String(row.title).trim() !== "",
      )
    : [];
  if (list.length === 0) return null;

  const rootClass = ["event-temp-event-body__schedule-list", className].filter(Boolean).join(" ");

  return (
    <ul className={rootClass}>
      {list.map((row, index) => (
        <li key={index} className="event-temp-event-body__schedule-item">
          <div className="event-temp-event-body__schedule-time">{row.time}</div>
          <div className="event-temp-event-body__schedule-main">
            <div className="event-temp-event-body__schedule-title">{row.title}</div>
            {row.description != null && String(row.description).trim() !== "" ? (
              <p className="event-temp-event-body__schedule-desc">{row.description}</p>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Muted bullet list for secondary notes. Use inside `EventBody` `body`, forms, etc.
 * Optional `title` renders a small label above the list (e.g. "Note").
 */
export function EventBodyNoteList({ items = [], title, className = "" } = {}) {
  const list = Array.isArray(items) ? items.filter((t) => t != null && String(t).trim() !== "") : [];
  if (list.length === 0) return null;

  const rootClass = ["event-temp-event-body__note", className].filter(Boolean).join(" ");
  const showTitle = title != null && String(title).trim() !== "";

  return (
    <div className={rootClass}>
      {showTitle ? <p className="event-temp-event-body__note-title">{title}</p> : null}
      <ul className="event-temp-event-body__note-list">
        {list.map((text, index) => (
          <li key={index} className="event-temp-event-body__note-item">
            {text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EventBodyImage({
  imageName,
  imageAlt = "",
  caption,
  mobileImageMaxWidth = "768px",
} = {}) {
  const imageSrc = imageName != null && String(imageName).trim() !== "" ? resolveCampImage(imageName) : undefined;
  if (!imageSrc) return null;

  const mobileSrc = resolveCampMobileImage(imageName);

  const img = (
    <img
      className="event-temp-event-body__img"
      src={imageSrc}
      alt={imageAlt}
      decoding="async"
    />
  );

  return (
    <figure className="event-temp-event-body__figure">
      <div className="event-temp-event-body__img-wrap">
        {mobileSrc ? (
          <picture>
            <source media={`(max-width: ${mobileImageMaxWidth})`} srcSet={mobileSrc} />
            {img}
          </picture>
        ) : (
          img
        )}
      </div>
      {caption != null && String(caption).trim() !== "" ? (
        <figcaption className="event-temp-event-body__caption">{caption}</figcaption>
      ) : null}
    </figure>
  );
}

/**
 * Instructor / article block: title + body on the left; optional **media as `children`** on the right.
 * Omit `children` (or pass nothing renderable) to let the text column span the full width.
 */
export default function EventBody({
  titlePrimary,
  titleSecondary,
  body,
  children,
  className = "",
  id,
} = {}) {
  const hasMedia = hasRenderableMedia(children);

  const rootClass = [
    "event-temp-event-body",
    hasMedia ? "" : "event-temp-event-body--no-media",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      id={id}
      className={rootClass}
      aria-label={titlePrimary != null ? String(titlePrimary) : undefined}
    >
      <div className="event-temp-event-body__inner">
        <div className="event-temp-event-body__text">
          {(titlePrimary != null && String(titlePrimary).trim() !== "") ||
          (titleSecondary != null && String(titleSecondary).trim() !== "") ? (
            <h2 className="event-temp-event-body__title">
              {titlePrimary != null && String(titlePrimary).trim() !== "" ? (
                <span className="event-temp-event-body__title-primary">{titlePrimary}</span>
              ) : null}
              {titleSecondary != null && String(titleSecondary).trim() !== "" ? (
                <span className="event-temp-event-body__title-secondary">{titleSecondary}</span>
              ) : null}
            </h2>
          ) : null}
          {body != null ? <div className="event-temp-event-body__content">{body}</div> : null}
        </div>
        {hasMedia ? <div className="event-temp-event-body__media">{children}</div> : null}
      </div>
    </section>
  );
}
